import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { 
  Dashboard, Login, Map, NotFound, Events, 
  Organizers, Providers, Sponsors, Rewards 
} from "./pages";
import { Main } from "./layouts";
import { useEffect, useState } from "react";
import { Spin, message } from "antd";
import axios from 'axios';
import jwt_decode from "jwt-decode";
import apiService from './services/apiService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  // Configuration globale d'Axios
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token")
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("token")
        setIsAuthenticated(false)
        setUser(null)
        message.error("Session expirée. Veuillez vous reconnecter.")
        return Promise.reject(error)
      }
      if (error.response?.status === 403) {
        message.error("Accès refusé - Droits administrateur requis")
        return Promise.reject(error)
      }
      return Promise.reject(error)
    }
  )

  const verifyAdminToken = (token) => {
    try {
      const decoded = jwt_decode(token)
      return decoded.exp * 1000 > Date.now() && decoded.role === "admin"
    } catch {
      return false
    }
  }

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token")

      if (token && verifyAdminToken(token)) {
        try {
          const response = await axios.get(
            apiService.admin.verifyUser(),
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )

          if (response.data.user.role === "admin") {
            setUser(response.data.user)
            setIsAuthenticated(true)
          } else {
            throw new Error("Not admin")
          }
        } catch (error) {
          localStorage.removeItem("token")
          setIsAuthenticated(false)
          message.error("Accès non autorisé")
        }
      } else {
        localStorage.removeItem("token")
        setIsAuthenticated(false)
      }

      setLoading(false)
    }

    checkAuth()
  }, [])

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token")
      if (token) {
        await axios.post(
          apiService.admin.logout(),
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
      }
      localStorage.removeItem("token")
      setIsAuthenticated(false)
      setUser(null)
      message.success("Déconnexion réussie")
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error)
      // Même en cas d'erreur, on nettoie les données locales
      localStorage.removeItem("token")
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-neutral-50">
        <Spin
          size="large"
          tip="Chargement..."
        />
      </div>
    )
  }

  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      message.error("Veuillez vous connecter pour accéder à cette page")
      return (
        <Navigate
          to="/login"
          replace
        />
      )
    }
    if (!user || user.role !== "admin") {
      message.error("Accès réservé aux administrateurs")
      return (
        <Navigate
          to="/login"
          replace
        />
      )
    }
    return children
  }

  const PublicRoute = ({ children }) => {
    if (isAuthenticated) {
      return (
        <Navigate
          to="/"
          replace
        />
      )
    }
    return children
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login
                setIsAuthenticated={setIsAuthenticated}
                setUser={setUser}
              />
            </PublicRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Main
                user={user}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={<Dashboard />}
          />
          <Route
            path="dashboard"
            element={<Dashboard />}
          />
          <Route
            path="map"
            element={<Map />}
          />
          <Route
            path="events"
            element={<Events />}
          />
          <Route
            path="organizers"
            element={<Organizers />}
          />
          <Route
            path="providers"
            element={<Providers />}
          />
          <Route
            path="sponsors"
            element={<Sponsors />}
          />
          <Route
            path="rewards"
            element={<Rewards />}
          />
        </Route>

        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>
    </BrowserRouter>
  )
}

export default App;
